'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/db/supabase';
import { useUserStore } from '@/lib/store/user-store';
import { Session } from '@supabase/supabase-js';
interface AuthContextType {
  session: Session | null;
  isLoading: boolean;
}
const AuthContext = createContext<AuthContextType>({
  session: null,
  isLoading: true
});
export const useAuth = () => useContext(AuthContext);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    fetchUserByEmail,
    setCurrentUser
  } = useUserStore();
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        const {
          data: {
            session: initialSession
          }
        } = await supabase.auth.getSession();
        setSession(initialSession);
        if (initialSession?.user) {
          // Fetch user data from our database
          await fetchUserByEmail(initialSession.user.email || '');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    initializeAuth();

    // Set up auth state change listener
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession);
      if (event === 'SIGNED_IN' && newSession?.user) {
        await fetchUserByEmail(newSession.user.email || '');
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
      }
    });

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserByEmail, setCurrentUser]);
  return <AuthContext.Provider value={{
    session,
    isLoading
  }}>
      {children}
    </AuthContext.Provider>;
}